/**
 * Geometry Tab
 * 
 * TODO: Copy from SMBeamDesigner.tsx lines 1473-1817
 * Look for: {activeStep === 'geometry' && (
 * Copy everything inside including the <div> wrapper
 */

import React from 'react'
import { SMBeamModel, SMBeamNode, SectionMaterialStrength, CrossSection, UiNotice } from '../types'
import { BeamKonvaVisualization, CrossSectionKonva } from '../components/BeamVisualizationKit'
import CrossSectionDetailsTable from '../components/CrossSectionDetailsTable'
import { buildRebarZones, defaultRebarSpecFor } from '../concreteRebarPresets'
import { summarizeShearReinforcement } from '../shearReinforcement'
import { ResizablePanels, NumberInput } from '@structura/ui'
import { useResizablePanels } from '@structura/ui'
import { useTranslation, TranslationKey } from '@structura/i18n'

// Support type → translation key, so the four support buttons render localized labels.
const SUPPORT_LABEL_KEY: Record<SMBeamNode['supportType'], TranslationKey> = {
  Fixed: 'beam.support.fixed',
  Pinned: 'beam.support.pinned',
  Roller: 'beam.support.roller',
  Free: 'beam.support.free'
}

/**
 * Recalculate layer z-positions based on cover, stirrup diameter, bar diameters and section height.
 * Per EC2 4.4.1: nominal cover (cnom) is measured to outermost reinforcement (stirrups).
 * Main bar center distance from surface = cnom + Ø_stirrup + Ø_bar/2.
 * Clear gap between stacked layers ≥ max(Ø_prev, Ø_next, 20 mm) per EC2 8.2.
 * z=0 at section centroid; negative = bottom, positive = top.
 */
function recalcLayerPositions(
  layers: Array<{ z: number; diameter: number; count: number }>,
  sectionHeight: number,
  cover: number,
  stirrupDiameter: number
): Array<{ z: number; diameter: number; count: number }> {
  if (!layers || layers.length === 0) return layers

  const result = layers.map(l => ({ ...l }))

  // Separate by side, keeping original indices
  const bottom = layers
    .map((l, i) => ({ ...l, idx: i }))
    .filter(l => l.z < 0)
    .sort((a, b) => a.z - b.z) // most negative first = outermost

  const top = layers
    .map((l, i) => ({ ...l, idx: i }))
    .filter(l => l.z > 0)
    .sort((a, b) => b.z - a.z) // most positive first = outermost

  // Layers at z === 0 are left unchanged (explicit center placement)

  // Recalculate bottom layers from outside in
  let prevZ = 0, prevD = 0
  for (let i = 0; i < bottom.length; i++) {
    const d = bottom[i].diameter
    if (i === 0) {
      bottom[i].z = -(sectionHeight / 2 - cover - stirrupDiameter - d / 2)
    } else {
      const clearGap = Math.max(prevD, d, 20)
      bottom[i].z = prevZ + prevD / 2 + clearGap + d / 2
    }
    prevZ = bottom[i].z
    prevD = d
    result[bottom[i].idx] = { z: Math.round(bottom[i].z), diameter: d, count: bottom[i].count }
  }

  // Recalculate top layers from outside in
  prevZ = 0; prevD = 0
  for (let i = 0; i < top.length; i++) {
    const d = top[i].diameter
    if (i === 0) {
      top[i].z = sectionHeight / 2 - cover - stirrupDiameter - d / 2
    } else {
      const clearGap = Math.max(prevD, d, 20)
      top[i].z = prevZ - prevD / 2 - clearGap - d / 2
    }
    prevZ = top[i].z
    prevD = d
    result[top[i].idx] = { z: Math.round(top[i].z), diameter: d, count: top[i].count }
  }

  return result
}

interface GeometryTabProps {
  model: SMBeamModel
  setModel: React.Dispatch<React.SetStateAction<SMBeamModel>>
  currentMaterial: string
  timberKind: 'glulam' | 'solid'
  addSpan: () => void
  removeSpan: (spanId: number) => void
  updateNodeSupport: (nodeId: number, supportType: SMBeamNode['supportType']) => void
  updateSpanLength: (spanId: number, newLength: number) => void
  getFilteredSectionPresets: () => Record<string, CrossSection>
  getSupportIcon: (supportType: SMBeamNode['supportType']) => string
  getSupportColor: (supportType: SMBeamNode['supportType']) => string
  sectionMaterialStrength: SectionMaterialStrength | null
  supportNotice?: UiNotice | null
  onDismissSupportNotice?: () => void
  sectionCatalogLoading?: boolean
  sectionCatalogError?: string | null
}

const GeometryTab: React.FC<GeometryTabProps> = ({ 
  model, 
  setModel,
  currentMaterial,
  timberKind,
  addSpan,
  removeSpan,
  updateNodeSupport,
  updateSpanLength,
  getFilteredSectionPresets,
  getSupportIcon,
  getSupportColor,
  sectionMaterialStrength,
  supportNotice,
  onDismissSupportNotice,
  sectionCatalogLoading,
  sectionCatalogError
}) => {
  const { t } = useTranslation()
  // The MODEL is the source of truth for the Tretype: loading a saved design replaces the model
  // (section + materialSelection) atomically, while the timberKind PROP lags one render behind
  // (the material UI state adopts the loaded selection in an effect). Filtering with the stale
  // prop made the snap effect below replace a freshly loaded design's section with a preset of
  // the WRONG kind before adoption caught up.
  const effectiveTimberKind: 'glulam' | 'solid' = model.materialSelection?.timberKind ?? timberKind
  // Section presets for the dropdown: for timber, show only the sections matching the selected
  // Tretype (limtre vs konstruksjonsvirke); other materials show their full filtered set.
  const visibleSectionPresets = (): Record<string, CrossSection> => {
    const all = getFilteredSectionPresets()
    if (currentMaterial !== 'timber') {
      return all
    }
    // Standard sections are tagged solid/glulam (filter by the selected Tretype). Untagged timber
    // sections are custom user sections with no Tretype — keep those visible under either type.
    // Order by width then height so the list starts with the narrowest series (e.g. 90x for Limtre).
    const filtered = Object.entries(all).filter(([, section]) => !section.timberKind || section.timberKind === effectiveTimberKind)
    filtered.sort(([, a], [, b]) => a.width - b.width || a.height - b.height)
    return Object.fromEntries(filtered)
  }

  // Keep the dropdown selection valid so the section panel and the select always agree: if the current
  // (non-custom) section is not among the visible presets — an unknown key (e.g. a stale default not in
  // the catalog) or one hidden by the Tretype filter — snap to the first visible one (narrowest, since
  // the list is width-sorted).
  React.useEffect(() => {
    const current = model.crossSection
    if (current.isCustom) {
      return
    }
    const visible = visibleSectionPresets()
    if (current.key && visible[current.key]) {
      return
    }
    const replacement = Object.values(visible)[0]
    if (!replacement) {
      return
    }
    setModel(prev => ({
      ...prev,
      crossSection: replacement,
      spans: prev.spans.map(span => ({ ...span, A: replacement.area, I: replacement.inertia }))
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMaterial, effectiveTimberKind, getFilteredSectionPresets, model.crossSection, setModel])

  const { leftWidth, rightWidth, handleMouseDown, isDragging, containerRef } = useResizablePanels({
    defaultLeftWidth: 30,
    minLeftWidth: 3,
    maxLeftWidth: 97,
    storageKey: 'beamDesigner_geometryTab_leftWidth'
  })

  const noticeColor = supportNotice?.variant === 'warning'
    ? 'warning'
    : supportNotice?.variant === 'danger'
      ? 'danger'
      : supportNotice?.variant === 'success'
        ? 'success'
        : 'primary'

  const leftPanel = (
    <div>
        <div>
            {supportNotice && (
                <div className="mb-4">
                    <div className={`p-3 border border-${noticeColor} rounded-3 bg-light`}
                        role="status"
                        aria-live="polite">
                        <div className="d-flex justify-content-between align-items-start">
                            <div className="d-flex">
                                <i className={`bi bi-info-circle-fill text-${noticeColor} me-2 mt-1`}></i>
                                <div>
                                    <h6 className={`mb-1 text-${noticeColor}`}>{supportNotice.title}</h6>
                                    {supportNotice.description && (
                                        <p className="mb-2 text-muted">{supportNotice.description}</p>
                                    )}
                                </div>
                            </div>
                            {onDismissSupportNotice && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={onDismissSupportNotice}
                                    aria-label={t('beam.geometry.dismissNotice')}
                                >
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            )}
                        </div>
                        {supportNotice.bullets && supportNotice.bullets.length > 0 && (
                            <ul className="mb-0 mt-2 ps-3">
                                {supportNotice.bullets.map((bullet, index) => (
                                    <li key={`${supportNotice.id ?? 'notice'}-${index}`} className="mb-1">
                                        {bullet}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6>{t('beam.geometry.beamSpans', { count: model.spans.length })}</h6>
                <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={addSpan}
                >
                    <i className="bi bi-plus"></i> {t('beam.geometry.addSpan')}
                </button>
            </div>

            {/* Spans List */}
            <div className="mb-4">
                <label className="form-label fw-bold">{t('beam.geometry.currentSpans')}</label>
                {model.spans.map(span => (
                    <div key={span.id} className="d-flex justify-content-between align-items-center p-2 border rounded mb-2">
                        <div>
                            <strong>{t('beam.geometry.spanNumber', { n: span.id })}</strong>: {span.length}m
                            <small className="text-muted d-block">
                                {t('beam.geometry.spanMeta', { left: span.leftNodeId, right: span.rightNodeId, e: (span.E / 1e9).toFixed(0), a: (span.A * 1e4).toFixed(1), i: (span.I * 1e8).toFixed(0) })}
                            </small>
                            {span.loads.length > 0 && (
                                <small className="text-info d-block">
                                    <i className="bi bi-info-circle me-1"></i>{t('beam.geometry.loadsApplied', { count: span.loads.length })}
                                </small>
                            )}
                        </div>
                        <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeSpan(span.id)}
                            title={t('beam.geometry.removeSpan')}
                            disabled={model.spans.length <= 1}
                        >
                            <i className="bi bi-trash"></i>
                        </button>
                    </div>
                ))}
            </div>

            {/* Nodes and Supports */}
            <div className="mb-4">
                <label className="form-label fw-bold">{t('beam.geometry.supportConditions')}</label>
                {model.nodes.map((node) => (
                    <div key={node.id} className="d-flex align-items-center mb-2">
                        <span className="me-2">{t('beam.geometry.node', { id: node.id })}</span>
                        <div className="btn-group btn-group-sm">
                            {(['Fixed', 'Pinned', 'Roller', 'Free'] as const).map(type => (
                                <button
                                    key={type}
                                    className={`btn btn-outline-${getSupportColor(type)} ${node.supportType === type ? 'active' : ''}`}
                                    onClick={() => updateNodeSupport(node.id, type)}
                                >
                                    {getSupportIcon(type)} {t(SUPPORT_LABEL_KEY[type])}
                                </button>
                            ))}
                        </div>
                        {currentMaterial === 'timber' && node.supportType !== 'Free' && (
                            <div className="input-group input-group-sm ms-2" style={{ maxWidth: '11rem' }} title={t('beam.geometry.bearingLengthHint')}>
                                <span className="input-group-text">{t('beam.geometry.bearingLength')}</span>
                                <NumberInput
                                    value={node.bearingLengthMm ?? null}
                                    onCommit={(parsed) => {
                                        const bearingLengthMm: number | undefined = parsed > 0 ? parsed : undefined
                                        setModel(previous => ({
                                            ...previous,
                                            nodes: previous.nodes.map(existing => existing.id === node.id ? { ...existing, bearingLengthMm } : existing)
                                        }))
                                    }}
                                    onClear={() => {
                                        setModel(previous => ({
                                            ...previous,
                                            nodes: previous.nodes.map(existing => existing.id === node.id ? { ...existing, bearingLengthMm: undefined } : existing)
                                        }))
                                    }}
                                />
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Cross Section Selection */}
            <div className="mb-4">
                <label className="form-label fw-bold">{t('beam.geometry.crossSection')}</label>
                {sectionCatalogLoading && (
                    <div className="alert alert-info py-2 px-3 mb-3" role="status">
                        <i className="bi bi-arrow-repeat me-2" aria-hidden="true"></i>
                        {t('beam.geometry.loadingCatalog')}
                    </div>
                )}
                {sectionCatalogError && (
                    <div className="alert alert-warning py-2 px-3 mb-3" role="status">
                        <i className="bi bi-exclamation-triangle me-2" aria-hidden="true"></i>
                        {t('beam.geometry.catalogError', { error: sectionCatalogError })}
                    </div>
                )}
                <div className="row">
                    <div className="col-8">
                        <select
                            className="form-select"
                            value={
                                model.crossSection.isCustom
                                    ? ''
                                    : (model.crossSection.key || '')
                            }
                            onChange={(e) => {
                                if (e.target.value) {
                                    // Select a preset (only if value is not empty)
                                    const filteredSections = visibleSectionPresets()
                                    const selectedSection = filteredSections[e.target.value]
                                    if (selectedSection) {
                                        setModel(prev => {
                                            const next: SMBeamModel = {
                                                ...prev,
                                                crossSection: selectedSection,
                                                spans: prev.spans.map(span => ({
                                                    ...span,
                                                    A: selectedSection.area,
                                                    I: selectedSection.inertia
                                                }))
                                            }
                                            // Predefined concrete sections seed their curated reinforcement.
                                            if (selectedSection.material === 'concrete') {
                                                const beamLength = prev.spans.reduce((sum, span) => sum + span.length, 0)
                                                next.rebarZones = buildRebarZones(selectedSection, defaultRebarSpecFor(selectedSection), beamLength)
                                            }
                                            return next
                                        })
                                    }
                                }
                            }}
                        >
                            <option value="" disabled>{t('beam.geometry.selectPresetPlaceholder')}</option>
                            {Object.entries(visibleSectionPresets()).map(([key, section]) => (
                                <option key={key} value={key}>
                                    {section.name} ({section.width}×{section.height}mm)
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-4">
                        <button
                            className="btn btn-outline-secondary w-100"
                            onClick={() => {
                                setModel(prev => ({
                                    ...prev,
                                    crossSection: {
                                        ...prev.crossSection,
                                        name: 'Custom',
                                        isCustom: true,
                                        key: undefined  // Remove key to indicate custom
                                    }
                                }))
                            }}
                        >
                            <i className="bi bi-pencil me-2"></i>
                            {t('beam.geometry.custom')}
                        </button>
                    </div>
                </div>

                {/* Custom Section Input */}
                {model.crossSection.isCustom && (
                    <div className="mt-3">
                        <div className="text-muted mb-3">
                            <small>
                                <i className="bi bi-info-circle me-2"></i>
                                {t('beam.geometry.customSectionHint')}
                            </small>
                        </div>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label fw-bold">{t('beam.geometry.width')}</label>
                                <NumberInput
                                    value={model.crossSection.width}
                                    onCommit={(width) => {
                                        const height = model.crossSection.height
                                        const widthM = width / 1000  // Convert mm to m
                                        const heightM = height / 1000  // Convert mm to m
                                        const area = widthM * heightM  // m²
                                        const inertia = (widthM * Math.pow(heightM, 3)) / 12  // m⁴
                                        setModel(prev => ({
                                            ...prev,
                                            crossSection: { ...prev.crossSection, width, area, inertia },
                                            spans: prev.spans.map(span => ({ ...span, A: area, I: inertia }))
                                        }))
                                    }}
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-bold">{t('beam.geometry.height')}</label>
                                <NumberInput
                                    value={model.crossSection.height}
                                    onCommit={(height) => {
                                        const width = model.crossSection.width
                                        const widthM = width / 1000  // Convert mm to m
                                        const heightM = height / 1000  // Convert mm to m
                                        const area = widthM * heightM  // m²
                                        const inertia = (widthM * Math.pow(heightM, 3)) / 12  // m⁴
                                        setModel(prev => ({
                                            ...prev,
                                            crossSection: { ...prev.crossSection, height, area, inertia },
                                            spans: prev.spans.map(span => ({ ...span, A: area, I: inertia }))
                                        }))
                                    }}
                                />
                            </div>

                            {/* Optional: Web and Flange thickness for steel I-sections */}
                            {currentMaterial === 'steel' && (
                                <>
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            {t('beam.geometry.webThickness')}
                                            <small className="text-muted ms-1">{t('beam.geometry.optionalIH')}</small>
                                        </label>
                                        <NumberInput
                                            value={model.crossSection.webThickness ?? null}
                                            placeholder={t('beam.geometry.egExample6')}
                                            onCommit={(webThickness) => {
                                                setModel(prev => ({
                                                    ...prev,
                                                    crossSection: { ...prev.crossSection, webThickness }
                                                }))
                                            }}
                                            onClear={() => {
                                                setModel(prev => ({ ...prev, crossSection: { ...prev.crossSection, webThickness: undefined } }))
                                            }}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            {t('beam.geometry.flangeThickness')}
                                            <small className="text-muted ms-1">{t('beam.geometry.optionalIH')}</small>
                                        </label>
                                        <NumberInput
                                            value={model.crossSection.flangeThickness ?? null}
                                            placeholder={t('beam.geometry.egExample10')}
                                            onCommit={(flangeThickness) => {
                                                setModel(prev => ({
                                                    ...prev,
                                                    crossSection: { ...prev.crossSection, flangeThickness }
                                                }))
                                            }}
                                            onClear={() => {
                                                setModel(prev => ({ ...prev, crossSection: { ...prev.crossSection, flangeThickness: undefined } }))
                                            }}
                                        />
                                    </div>
                                    <div className="col-12">
                                        <small className="text-muted">
                                            <i className="bi bi-lightbulb me-1"></i>
                                            {t('beam.geometry.webFlangeHint')}
                                        </small>
                                    </div>
                                </>
                            )}

                            {/* Display calculated values */}
                            <div className="col-12">
                                <div className="card bg-light">
                                    <div className="card-body py-2">
                                        <small>
                                            <strong>{t('beam.geometry.calculatedProps')}</strong><br />
                                            {t('beam.geometry.areaValue', { cm2: (model.crossSection.area * 1e4).toFixed(2), mm2: (model.crossSection.area * 1e6).toFixed(0) })}<br />
                                            {t('beam.geometry.inertiaValue', { cm4: (model.crossSection.inertia * 1e8).toFixed(2), mm4: (model.crossSection.inertia * 1e12).toFixed(0) })}
                                        </small>
                                    </div>
                                </div>
                            </div>

                            {/* Button to go back to presets */}
                            <div className="col-12">
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => {
                                        const presets = visibleSectionPresets()
                                        const firstKey = Object.keys(presets)[0]
                                        const firstSection = presets[firstKey]
                                        if (firstSection) {
                                            setModel(prev => ({
                                                ...prev,
                                                crossSection: firstSection,
                                                spans: prev.spans.map(span => ({
                                                    ...span,
                                                    A: firstSection.area,
                                                    I: firstSection.inertia
                                                }))
                                            }))
                                        }
                                    }}
                                >
                                    <i className="bi bi-arrow-left me-2"></i>
                                    {t('beam.geometry.backToStandard')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Section Classification Override (Steel only) */}
            {currentMaterial === 'steel' && (
                <div className="mb-4">
                    <label className="form-label fw-bold">
                        {t('beam.geometry.sectionClassTitle')}
                        <i
                            className="bi bi-info-circle ms-2 text-muted"
                            title={t('beam.geometry.sectionClassHelp')}
                            style={{ cursor: 'help' }}
                        ></i>
                    </label>
                    <select
                        className="form-select"
                        value={model.crossSection.sectionClass || 'auto'}
                        onChange={(e) => {
                            setModel(prev => ({
                                ...prev,
                                crossSection: {
                                    ...prev.crossSection,
                                    sectionClass: e.target.value as 'auto' | '1' | '2' | '3' | '4'
                                }
                            }))
                        }}
                    >
                        <option value="auto">{t('beam.geometry.classAuto')}</option>
                        <option value="1">{t('beam.geometry.class1')}</option>
                        <option value="2">{t('beam.geometry.class2')}</option>
                        <option value="3">{t('beam.geometry.class3')}</option>
                        <option value="4">{t('beam.geometry.class4')}</option>
                    </select>
                    <small className="text-muted d-block mt-1">
                        {model.crossSection.sectionClass === 'auto' || !model.crossSection.sectionClass
                            ? t('beam.geometry.classAutoNote')
                            : t('beam.geometry.classOverrideNote', { n: model.crossSection.sectionClass })}
                    </small>
                </div>
            )}

            {/* Span Lengths */}
            <div>
                <label className="form-label fw-bold">{t('beam.geometry.spanLengths')}</label>
                {model.spans.map((span) => (
                    <div key={span.id} className="mb-2">
                        <label className="form-label">{t('beam.geometry.spanNumber', { n: span.id })}:</label>
                        {/* Blur-committed: updateSpanLength confirms/removes out-of-range loads, so an
                            intermediate keystroke value must never be applied. */}
                        <NumberInput
                            value={span.length}
                            commitOnBlur
                            onCommit={(v) => updateSpanLength(span.id, v)}
                        />
                        {span.loads.length > 0 && (
                            <small className="text-muted d-block mt-1">
                                <i className="bi bi-info-circle me-1"></i>
                                {t('beam.geometry.lengthChangeHint')}
                            </small>
                        )}
                    </div>
                ))}
            </div>
        </div>
      </div>
  )

  const rightPanel = (
    <div>
      {/* Right Panel - Visualization */}
        <div className="card mb-3">
          <div className="card-header">
            <h5 className="card-title mb-0">{t('beam.header.geometry')}</h5>
          </div>
          <div className="card-body">
            <BeamKonvaVisualization 
              model={model} 
              material={currentMaterial} 
              width={600} 
              height={300}
              storageKey="beamVisualization_geometry"
            />
          </div>
        </div>
        
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">{t('beam.geometry.crossSection')}</h5>
          </div>
          <div className="card-body">
            {model.crossSection ? (
              <div className="row">
                <div className="col-md-6">
                  <CrossSectionKonva 
                    crossSection={model.crossSection} 
                    material={currentMaterial}
                    timberKind={currentMaterial === 'timber' ? timberKind : undefined}
                    width={400} 
                    height={300}
                    {...(currentMaterial === 'concrete' ? {
                      rebar: {
                        cover: ((model as any).rebarZones?.[0]?.cover) ?? 30,
                        layers: (((model as any).rebarZones?.[0]?.layers) ?? []).map((l: any) => ({ z: l.z, diameter: l.diameter, count: l.count })),
                        stirrups: (model as any).rebarZones?.[0]?.stirrups ? {
                          diameter: (model as any).rebarZones[0].stirrups.diameter,
                          spacing: (model as any).rebarZones[0].stirrups.spacing,
                          legs: (model as any).rebarZones[0].stirrups.legs
                        } : undefined
                      }
                    } : {})}
                  />
                </div>
                <div className="col-md-6">
                  <CrossSectionDetailsTable
                    crossSection={model.crossSection}
                    material={model.crossSection.material || currentMaterial}
                    timberKind={(model.crossSection.material || currentMaterial) === 'timber' ? timberKind : undefined}
                    materialStrength={sectionMaterialStrength ?? undefined}
                    shearReinforcement={summarizeShearReinforcement(model)}
                    onMomentCapacitiesCalculated={(capacities) => {
                      setModel(prev => {
                        const cs = prev.crossSection
                        // Check equality to prevent infinite render loops
                        if (cs.elasticMomentCapacityKNm === capacities.elasticMomentCapacityKNm &&
                            cs.plasticMomentCapacityKNm === capacities.plasticMomentCapacityKNm &&
                            cs.reinforcedMomentCapacityKNm === capacities.reinforcedMomentCapacityKNm) {
                          return prev
                        }
                        return {
                          ...prev,
                          crossSection: {
                            ...cs,
                            elasticMomentCapacityKNm: capacities.elasticMomentCapacityKNm,
                            plasticMomentCapacityKNm: capacities.plasticMomentCapacityKNm,
                            reinforcedMomentCapacityKNm: capacities.reinforcedMomentCapacityKNm
                          }
                        }
                      })
                    }}
                    onSectionPropertiesCalculated={(properties) => {
                      setModel(prev => {
                        const cs = prev.crossSection
                        // Check equality to prevent infinite render loops
                        if (cs.elasticModulusY === properties.elasticModulusY &&
                            cs.plasticModulusY === properties.plasticModulusY &&
                            cs.effectiveDepth === properties.effectiveDepth) {
                          return prev
                        }
                        return {
                          ...prev,
                          crossSection: {
                            ...cs,
                            ...properties
                          }
                        }
                      })
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="text-muted">{t('beam.geometry.loadingCrossSection')}</div>
            )}
          </div>
        </div>

        {currentMaterial.toLowerCase() === 'concrete' && (
        <div className="card mt-3">
          <div className="card-header">
            <h5 className="card-title mb-0">{t('beam.geometry.reinforcement')}</h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label fw-bold">{t('beam.geometry.rebarZones')}</label>
                <small className="text-muted d-block">{t('beam.geometry.rebarZonesHint')}</small>
              </div>
              <div className="col-12">
                <button className="btn btn-sm btn-outline-primary" onClick={() => {
                  (setModel as any)((prev: any) => ({
                    ...prev,
                    rebarZones: [ ...(prev.rebarZones ?? []), { start: 0, end: (prev.spans?.[0]?.length ?? 1), cover: 30, layers: [], stirrups: undefined } ]
                  }))
                }}>{t('beam.geometry.addZone')}</button>
              </div>
              {(model as any).rebarZones?.map((zone: any, zi: number) => (
                <div key={zi} className="col-12 border rounded p-2">
                  <div className="row g-2 align-items-end">
                    <div className="col-3">
                      <label className="form-label">{t('beam.geometry.zoneStart')}</label>
                      <NumberInput value={zone.start} onCommit={(v) => {
                        (setModel as any)((prev: any) => {
                          const rz = [...(prev.rebarZones ?? [])]
                          rz[zi] = { ...rz[zi], start: v }
                          return { ...prev, rebarZones: rz }
                        })
                      }} />
                    </div>
                    <div className="col-3">
                      <label className="form-label">{t('beam.geometry.zoneEnd')}</label>
                      <NumberInput value={zone.end} onCommit={(v) => {
                        (setModel as any)((prev: any) => {
                          const rz = [...(prev.rebarZones ?? [])]
                          rz[zi] = { ...rz[zi], end: v }
                          return { ...prev, rebarZones: rz }
                        })
                      }} />
                    </div>
                    <div className="col-3">
                      <label className="form-label">{t('beam.geometry.zoneCover')}</label>
                      <NumberInput value={zone.cover ?? 30} onCommit={(v) => {
                        (setModel as any)((prev: any) => {
                          const rz = [...(prev.rebarZones ?? [])]
                          const stirD = rz[zi].stirrups?.diameter ?? 0
                          const updatedLayers = recalcLayerPositions(rz[zi].layers ?? [], prev.crossSection?.height ?? 0, v, stirD)
                          rz[zi] = { ...rz[zi], cover: v, layers: updatedLayers }
                          return { ...prev, rebarZones: rz }
                        })
                      }} />
                    </div>
                    <div className="col-3 d-flex align-items-end justify-content-end">
                      <button className="btn btn-sm btn-outline-danger" onClick={() => {
                        (setModel as any)((prev: any) => {
                          const rz = [...(prev.rebarZones ?? [])]
                          rz.splice(zi, 1)
                          return { ...prev, rebarZones: rz }
                        })
                      }}>{t('beam.geometry.removeZone')}</button>
                    </div>
                    <div className="col-12">
                      <div className="d-flex align-items-center">
                        <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => {
                          (setModel as any)((prev: any) => {
                            const rz = [...(prev.rebarZones ?? [])]
                            const zoneH = prev.crossSection?.height ?? 0
                            const cover = rz[zi].cover ?? 30
                            const stirD = rz[zi].stirrups?.diameter ?? 0
                            const dNew = 16
                            // Add with placeholder z < 0 so recalc places it as innermost bottom layer
                            const layersWithNew = [...(rz[zi].layers ?? []), { z: -0.001, diameter: dNew, count: 2 }]
                            const layers = recalcLayerPositions(layersWithNew, zoneH, cover, stirD)
                            rz[zi] = { ...rz[zi], layers }
                            return { ...prev, rebarZones: rz }
                          })
                        }}>{t('beam.geometry.addRebarBottom')}</button>
                        <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => {
                          (setModel as any)((prev: any) => {
                            const rz = [...(prev.rebarZones ?? [])]
                            const zoneH = prev.crossSection?.height ?? 0
                            const cover = rz[zi].cover ?? 30
                            const stirD = rz[zi].stirrups?.diameter ?? 0
                            const dNew = 16
                            // Add with placeholder z > 0 so recalc places it as innermost top layer
                            const layersWithNew = [...(rz[zi].layers ?? []), { z: 0.001, diameter: dNew, count: 2 }]
                            const layers = recalcLayerPositions(layersWithNew, zoneH, cover, stirD)
                            rz[zi] = { ...rz[zi], layers }
                            return { ...prev, rebarZones: rz }
                          })
                        }}>{t('beam.geometry.addRebarTop')}</button>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => {
                          (setModel as any)((prev: any) => {
                            const rz = [...(prev.rebarZones ?? [])]
                            const st = { diameter: 8, spacing: 200, legs: 2, ...(rz[zi].stirrups ?? {}) }
                            const cover = rz[zi].cover ?? 30
                            const updatedLayers = recalcLayerPositions(rz[zi].layers ?? [], prev.crossSection?.height ?? 0, cover, st.diameter)
                            rz[zi] = { ...rz[zi], stirrups: st, layers: updatedLayers }
                            return { ...prev, rebarZones: rz }
                          })
                        }}>{t('beam.geometry.addStirrups')}</button>
                      </div>
                      <div className="mt-2">
                        {zone.layers?.map((layer: any, li: number) => (
                          <div key={li} className="row g-2 align-items-end mb-1">
                            <div className="col-3">
                              <label className="form-label">{t('beam.geometry.layerZ')}</label>
                              <NumberInput value={layer.z} onCommit={(v) => {
                                (setModel as any)((prev: any) => {
                                  const rz = [...(prev.rebarZones ?? [])]
                                  const layers = [...(rz[zi].layers ?? [])]
                                  layers[li] = { ...layers[li], z: v }
                                  rz[zi] = { ...rz[zi], layers }
                                  return { ...prev, rebarZones: rz }
                                })
                              }} />
                            </div>
                            <div className="col-3">
                              <label className="form-label">{t('beam.geometry.layerDiameter')}</label>
                              <NumberInput value={layer.diameter} onCommit={(v) => {
                                (setModel as any)((prev: any) => {
                                  const rz = [...(prev.rebarZones ?? [])]
                                  const layers = [...(rz[zi].layers ?? [])]
                                  layers[li] = { ...layers[li], diameter: v }
                                  const cover = rz[zi].cover ?? 30
                                  const stirD = rz[zi].stirrups?.diameter ?? 0
                                  const updatedLayers = recalcLayerPositions(layers, prev.crossSection?.height ?? 0, cover, stirD)
                                  rz[zi] = { ...rz[zi], layers: updatedLayers }
                                  return { ...prev, rebarZones: rz }
                                })
                              }} />
                            </div>
                            <div className="col-3">
                              <label className="form-label">{t('beam.geometry.layerCount')}</label>
                              <NumberInput value={layer.count} integer onCommit={(v) => {
                                (setModel as any)((prev: any) => {
                                  const rz = [...(prev.rebarZones ?? [])]
                                  const layers = [...(rz[zi].layers ?? [])]
                                  layers[li] = { ...layers[li], count: v }
                                  rz[zi] = { ...rz[zi], layers }
                                  return { ...prev, rebarZones: rz }
                                })
                              }} />
                            </div>
                            <div className="col-3">
                              <button className="btn btn-sm btn-outline-danger" onClick={() => {
                                (setModel as any)((prev: any) => {
                                  const rz = [...(prev.rebarZones ?? [])]
                                  const layers = [...(rz[zi].layers ?? [])]
                                  layers.splice(li,1)
                                  rz[zi] = { ...rz[zi], layers }
                                  return { ...prev, rebarZones: rz }
                                })
                              }}>{t('beam.geometry.remove')}</button>
                            </div>
                          </div>
                        ))}
                        {zone.stirrups && (
                          <div className="row g-2 align-items-end mb-1">
                            <div className="col-3">
                              <label className="form-label">{t('beam.geometry.stirrupDiameter')}</label>
                              <NumberInput value={zone.stirrups?.diameter ?? 8} onCommit={(v) => {
                                (setModel as any)((prev: any) => {
                                  const rz = [...(prev.rebarZones ?? [])]
                                  const st = { ...(rz[zi].stirrups ?? {}), diameter: v }
                                  const cover = rz[zi].cover ?? 30
                                  const updatedLayers = recalcLayerPositions(rz[zi].layers ?? [], prev.crossSection?.height ?? 0, cover, v)
                                  rz[zi] = { ...rz[zi], stirrups: st, layers: updatedLayers }
                                  return { ...prev, rebarZones: rz }
                                })
                              }} />
                            </div>
                            <div className="col-3">
                              <label className="form-label">{t('beam.geometry.stirrupSpacing')}</label>
                              <NumberInput value={zone.stirrups?.spacing ?? 200} onCommit={(v) => {
                                (setModel as any)((prev: any) => {
                                  const rz = [...(prev.rebarZones ?? [])]
                                  const st = { ...(rz[zi].stirrups ?? {}), spacing: v }
                                  rz[zi] = { ...rz[zi], stirrups: st }
                                  return { ...prev, rebarZones: rz }
                                })
                              }} />
                            </div>
                            <div className="col-2">
                              <label className="form-label">{t('beam.geometry.stirrupLegs')}</label>
                              <NumberInput value={zone.stirrups?.legs ?? 2} integer onCommit={(v) => {
                                (setModel as any)((prev: any) => {
                                  const rz = [...(prev.rebarZones ?? [])]
                                  const st = { ...(rz[zi].stirrups ?? {}), legs: v }
                                  rz[zi] = { ...rz[zi], stirrups: st }
                                  return { ...prev, rebarZones: rz }
                                })
                              }} />
                            </div>
                            <div className="col-2 d-flex align-items-end">
                              <button className="btn btn-sm btn-outline-danger" onClick={() => {
                                (setModel as any)((prev: any) => {
                                  const rz = [...(prev.rebarZones ?? [])]
                                  const cover = rz[zi].cover ?? 30
                                  const updatedLayers = recalcLayerPositions(rz[zi].layers ?? [], prev.crossSection?.height ?? 0, cover, 0)
                                  rz[zi] = { ...rz[zi], stirrups: undefined, layers: updatedLayers }
                                  return { ...prev, rebarZones: rz }
                                })
                              }}>{t('beam.geometry.removeStirrups')}</button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        )}
    </div>
  )

  return (
    <ResizablePanels
      leftPanel={leftPanel}
      rightPanel={rightPanel}
      leftWidth={leftWidth}
      rightWidth={rightWidth}
      onMouseDown={handleMouseDown}
      isDragging={isDragging}
      containerRef={containerRef}
    />
  )
}

export default GeometryTab
